/**
 * What the part-definition wizard decides, apart from anything that draws it.
 *
 * A wizard is meant to be quick: the full model tree is where complex and
 * unusual changes belong, so every function here exists to make the SHORT path
 * correct rather than to expose everything the API will accept. Each one is
 * exported and pure because a wrong answer from any of them produces a form that
 * looks right and states the wrong model:
 *
 *   - `packageChoices` scopes and re-orders the package listing. `/api/packages`
 *     answers SHALLOWEST FIRST - every depth-1 package, then every depth-2 - so
 *     indenting the rows in the order they arrive draws `SystemDesign::GNC`
 *     underneath `Verification`. The depth is right and the order is not, which
 *     is precisely the mistake that renders a believable, wrong tree. It also
 *     narrows 28 packages to the six that hold parts, which is the whole point of
 *     scoping a picker per page.
 *   - `buildCreateBody` is the only place the request is assembled, and it must
 *     never emit `type_name`: the API refuses that field with a 422, because on a
 *     definition the declared type is a SPECIALIZATION, and having one name for
 *     "what is this a kind of" and "what does it sit inside" is how the wizard's
 *     two questions got confused in the first place. `specializes` is the field.
 *   - `definitionChoices` feeds the specializes picker and INCLUDES abstract
 *     definitions, since specializing a generic kind is the entire reason to have
 *     one. The child-part picker is fed from a different request altogether -
 *     `api.parts({usable_as_type: true})`, the server excluding abstract - so
 *     nothing here has to know what abstract means. Swapping those two sources is
 *     the error this file is arranged to make hard.
 *   - `describeCreated` reports what was made, because the person named none of
 *     it: a usage carries no `<ID>` and is named after its definition with a
 *     lowered first letter, made unique MODEL-WIDE, so the second usage of
 *     Guidance is `guidance2`. Every name is read off `usages[].name`; none is
 *     derived here, and there is deliberately no attempt to work out which ones
 *     were renamed - `CreatedUsage` carries the definition's ID and not its name,
 *     so that inference would be a guess.
 */

import type { PickerOption } from '../common/elements'
import { attributeOf, isCostly } from '../../schema'
import type {
  ChildPartRow,
  CreatedPart,
  FieldSchema,
  KindSchema,
  PackageRow,
  PartRow,
} from '../../schema'
import { childPartRows, childRowsProblem, emptyChildDraft } from './parts-editing'
import type { ChildDraft } from './parts-editing'

/**
 * The sentinel the package picker uses for its inline "new package" option.
 *
 * A value no qualified name can be, rather than an empty string or a label:
 * an empty string is "nothing chosen", and matching on a label would break the
 * moment the label were reworded. A LEADING '::' cannot begin a qualified name,
 * which is what makes this safe.
 *
 * It was a literal NUL byte first, which is the conventional trick and is wrong
 * in a source file: it made grep, and anything else sniffing for binary content,
 * treat this module as binary rather than text. tsc and vitest never minded,
 * which is exactly why it would have gone unnoticed.
 */
export const NEW_PACKAGE = '::new-package'

/** Why a definition may be marked generic, said where the flag is offered. */
export const ABSTRACT_HELP =
  'A generic kind: it may be specialized but never used directly. That is how ' +
  'Sensor and Thruster can exist as base types carrying attributes, while no ' +
  'design is allowed to contain a bare part x : Sensor.'

/**
 * Why a generic kind cannot be given a parent part.
 *
 * Worded from the API's own 422, which is the authority: the usage a parent part
 * asks for would be an instance of the new definition, and `abstract` is the
 * statement that there are none. Said here so it costs no round trip, and the
 * server's own refusal is still rendered if one ever arrives.
 */
export const ABSTRACT_HAS_NO_PARENT =
  'A generic kind has no instances, so it cannot be placed inside a parent part. ' +
  'Clear one or the other.'

export const ABSTRACT_HAS_NO_INSTANCE =
  'A generic kind has no instances, so it cannot be instanced. Untick one or the other.'

export const INSTANCE_HELP =
  'Also declare one of these in the package — an uncontained usage, which is the ' +
  'shape a design’s root has. Everything else in a design sits inside one.'

export const PACKAGE_HELP =
  'Where the definition itself goes. Scoped to the packages that already hold ' +
  'part definitions, plus the packages above them — the full model tree is on the ' +
  'Model page, for the unusual destination.'

export const SPECIALIZES_HELP =
  'What this is a KIND OF — the :> on the definition. Generic kinds are offered ' +
  'here on purpose; specializing one is the reason they exist.'

export const PARENT_HELP =
  'What this sits INSIDE. A usage of the new definition is created in this ' +
  'definition, so the part above it gains a child.'

export const CHILDREN_HELP =
  'A usage of each is created inside the new definition. Generic kinds are absent: ' +
  'the server offers only what may legally be the type of a usage.'

export const NEW_PACKAGE_NEEDS_PARENT =
  'A new package needs a parent to go in. BEST cannot yet decide which file would own ' +
  'a root package, so a package is added beneath an existing one.'

// ---------------------------------------------------------------------------
// The package picker
// ---------------------------------------------------------------------------

/** One row of the package picker, ready to indent. */
export interface PackageChoice {
  qualified_name: string
  name: string
  /**
   * How far to indent, counted along the rows that are actually SHOWN.
   *
   * Not `PackageRow.depth`, which is absolute: prune a package's parent away and
   * the absolute depth indents a visible root. These agree whenever the tree is
   * whole, and the one that agrees with the picture is the one to draw.
   */
  depth: number
  /**
   * True when this package already declares a part definition.
   *
   * The rest are its ancestors, kept so the tree renders connected rather than
   * as a list of orphans. They are still selectable - a package is a legal
   * destination whether or not it holds a part today.
   */
  holdsParts: boolean
}

/** The qualified name of what encloses this one, or '' at the top. */
function parentOf(qualified: string): string {
  const cut = qualified.lastIndexOf('::')
  return cut < 0 ? '' : qualified.slice(0, cut)
}

/**
 * The packages a new part definition may go in, scoped and in tree order.
 *
 * Two jobs, and both are load-bearing. The SCOPE is the packages that already
 * hold a part definition plus their ancestors: for the example program that is
 * six rows rather than twenty-eight, and it is what makes the picker answerable
 * at a glance. The ORDER is a pre-order walk, because the listing arrives
 * shallowest-first and indenting it as answered puts every depth-2 package under
 * whichever depth-1 package happened to come last.
 *
 * When nothing is in scope - a program with no part definitions yet, which is
 * exactly when somebody is making the first one - every package is offered.
 * Scoping to nothing would be a dead end rather than a shortcut.
 *
 * @param packages every package, as `GET /api/packages` answers it
 * @param parts every part, as `GET /api/parts` answers it
 * @param pinned qualified names to keep whatever the scope says - a package just
 *        created from inside this wizard holds no parts and is nobody's ancestor,
 *        so the scope would drop the very thing somebody just made
 */
export function packageChoices(
  packages: PackageRow[],
  parts: PartRow[],
  pinned: string[] = [],
): PackageChoice[] {
  const known = new Set(packages.map((row) => row.qualified_name))

  const holders = new Set<string>()
  for (const part of parts) {
    if (!part.is_definition) continue
    // A definition nested inside a part def has a part for a parent, which is
    // not in the package listing and so simply does not widen the scope.
    const owner = parentOf(String(part.qualified_name ?? ''))
    if (known.has(owner)) holders.add(owner)
  }

  const wanted = new Set(holders)
  for (const name of pinned) {
    if (known.has(name)) wanted.add(name)
  }
  // Ancestors of everything kept, so the tree renders connected rather than as a
  // list of orphans - a pinned package included, since it is usually deeper than
  // anything that holds a part.
  for (const kept of [...wanted]) {
    let up = parentOf(kept)
    while (up) {
      if (known.has(up)) wanted.add(up)
      up = parentOf(up)
    }
  }

  const kept = wanted.size
    ? packages.filter((row) => wanted.has(row.qualified_name))
    : packages
  const keptNames = new Set(kept.map((row) => row.qualified_name))

  // Children keyed by parent, in the order the listing answered them - which is
  // alphabetical within a depth, so each level stays alphabetical.
  const children = new Map<string, PackageRow[]>()
  const roots: PackageRow[] = []
  for (const row of kept) {
    const parent = parentOf(row.qualified_name)
    if (parent && keptNames.has(parent)) {
      const siblings = children.get(parent)
      if (siblings) siblings.push(row)
      else children.set(parent, [row])
    } else {
      roots.push(row)
    }
  }

  const ordered: PackageChoice[] = []
  const walk = (row: PackageRow, depth: number) => {
    ordered.push({
      qualified_name: row.qualified_name,
      name: row.name,
      depth,
      holdsParts: holders.has(row.qualified_name),
    })
    for (const child of children.get(row.qualified_name) ?? []) walk(child, depth + 1)
  }
  for (const root of roots) walk(root, 0)

  return ordered
}

// ---------------------------------------------------------------------------
// The part pickers
// ---------------------------------------------------------------------------

/** One part offered by a picker, identified by its ID rather than its name. */
export interface PartChoice {
  /**
   * The `<ID>`, which is what is sent.
   *
   * Not the name: the example program has `part def <GNC> Guidance` and a usage
   * also named Guidance, so a name is not a key. The API resolves an ID, a name
   * or a qualified name, and the ID is the one that cannot be ambiguous.
   */
  id: string
  name: string
  qualified_name: string
  /** Abstract - a generic kind. Shown so the choice is informed, not hidden. */
  isGeneric: boolean
}

function asChoice(row: PartRow): PartChoice {
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    qualified_name: String(row.qualified_name ?? ''),
    isGeneric: Boolean(row.is_abstract),
  }
}

/**
 * Every part DEFINITION, generic kinds included.
 *
 * This is what the "specializes" and "parent part" pickers offer, and including
 * abstract definitions is deliberate in both: `:> Sensor` is the reason Sensor
 * exists, and a usage may perfectly well be declared inside an abstract
 * definition's body. It is fed from `api.parts()` unfiltered.
 *
 * Usages are dropped because the API refuses one in either role with a 422 - a
 * usage cannot be specialized or decomposed.
 *
 * @param rows every part, as `GET /api/parts` answers it
 */
export function definitionChoices(rows: PartRow[]): PartChoice[] {
  return rows.filter((row) => row.is_definition).map(asChoice)
}

/**
 * The parts a child may be typed by, exactly as the server offered them.
 *
 * Nothing is filtered here, and that is the point: these rows come from
 * `api.parts({usable_as_type: true})`, which is the server answering "may this be
 * the declared type of a usage" - a definition, and not abstract. Re-deriving
 * that in the browser would mean knowing what abstract means, and getting it
 * wrong would offer a bare `part x : Sensor` that the API then refuses.
 *
 * @param rows the rows `usable_as_type` answered
 */
export function typeChoices(rows: PartRow[]): PartChoice[] {
  return rows.map(asChoice)
}

// ---------------------------------------------------------------------------
// The form itself
// ---------------------------------------------------------------------------

/**
 * What the person has filled in.
 *
 * There is no definition-or-usage choice, because there is no such choice to
 * make: a part is ALWAYS created as a definition, and usages exist only as
 * children. `parentPart` and `childParts` are how usages come about.
 */
export interface WizardForm {
  package: string
  identifier: string
  name: string
  abstract: boolean
  /**
   * Declare a usage of this definition in its package as well.
   *
   * A design's ROOT is an uncontained usage - `part vehicle : Vehicle;` sitting
   * in a package rather than inside another part - and every other part in the
   * design hangs off one. `parentPart` places a usage inside another PART, so
   * until this existed the root was the one thing the wizard could not make: it
   * had to be written by hand and imported.
   */
  instance: boolean
  specializes: string
  parentPart: string
  /**
   * A TABLE of rows rather than a list of IDs - Patrick's sixth observation.
   *
   * The multi-select it replaces could say which definitions go inside this one
   * and had nowhere to say how many of each, so `[4]` seekers were not
   * expressible at all. A row is a definition plus a multiplicity, which is
   * exactly the row `child_parts` takes.
   */
  childParts: ChildDraft[]
  /**
   * The remaining registry fields, keyed by field name.
   *
   * `unknown` rather than `string | boolean` because a quantity is a NUMBER on
   * the wire: `3.5`, not `'3.5'` and certainly not `'3.5 [SI::kg]'` - the server
   * composes the quantity from the field's own unit.
   */
  extras: Record<string, unknown>
  /**
   * Attributes to DECLARE on the new definition, as opposed to values to state.
   *
   * The difference matters and is easy to miss: everything in `extras` is a
   * value for an attribute the model already declares, and each of these
   * *creates* one. A quantity BEST has never heard of - a dewar's cooling power
   * - had to be typed into the `.sysml` file by hand before this existed.
   *
   * No value rides along, deliberately: the field does not exist until the
   * declaration does, so a value is stated on the edit that follows.
   */
  newAttributes: AttributeDraft[]
}

/**
 * Whether a row declares a number, a choice from a list, or - on a requirement
 * type only - a LINK to an element of the model (issue #48).
 */
export type AttributeShape = 'quantity' | 'choice' | 'link'

/**
 * What a requirement type may link its requirements to (issue #48), as the
 * server names the kinds. Each becomes `ref <keyword> name[0..1];` and the
 * `satisfy self by name;` that makes it a link.
 */
export const LINK_KINDS = [
  { value: 'interface', label: 'An interface' },
  { value: 'state', label: 'A state' },
  { value: 'design_port', label: 'A port' },
  { value: 'design_part', label: 'A part' },
  { value: 'function', label: 'A function' },
] as const

/** One attribute being declared on the part, before it is written. */
export interface AttributeDraft {
  /** Stable across re-renders, so a row keeps its focus while others change. */
  key: string
  name: string
  /**
   * A NUMBER or a CHOICE, and the two need different questions asked.
   *
   * A quantity needs its kind, which supplies the unit. A choice needs its
   * values, and BEST declares an `enum def` for them - which is what makes the
   * control a picker. A thruster's fuel is a text box today for exactly the
   * want of that declaration.
   */
  shape: AttributeShape
  /** An ISQ type from `/api/attribute-types`, e.g. `ISQ::PowerValue`. */
  declaredType: string
  /** For a choice: the members, as typed - `bare, diaphragm, piston`. */
  values: string
  /** For a link: the kind the far end must be - one of LINK_KINDS. */
  linkKind: string
  description: string
  /**
   * `@RollsUp`.
   *
   * Off by default, and that is not timidity: most quantities do NOT aggregate -
   * a temperature limit is not the sum of its parts' - and a wrong marker
   * produces a confident number rather than a missing one.
   */
  rollsUp: boolean
}

/** A blank attribute row. */
export function emptyAttributeDraft(key: string): AttributeDraft {
  return {
    key, name: '', shape: 'quantity', declaredType: '', values: '', linkKind: '',
    description: '', rollsUp: false,
  }
}

/**
 * The values of a choice row, split from what was typed.
 *
 * Commas OR newlines, because both are natural: a short list is typed on one
 * line and a long one is pasted from somewhere. Blanks are dropped so a
 * trailing comma is not an error.
 */
export function splitValues(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((value) => value.trim())
    .filter((value) => value !== '')
}

/**
 * The rows worth sending, as the create route wants them.
 *
 * A row with no name is one somebody opened and did not fill in, which is not an
 * error - the form offers a blank row so there is somewhere to type. It is
 * dropped rather than refused, exactly as a blank child-part row is.
 */
export function attributePayload(drafts: readonly AttributeDraft[]): {
  name: string
  declared_type: string
  description: string
  rolls_up: boolean
  values?: string[]
  link?: string
}[] {
  return drafts
    .filter((draft) => draft.name.trim() !== '')
    .map((draft) => ({
      name: draft.name.trim(),
      // A choice names no type: the server derives the list's name from the
      // attribute, which is what keeps `tankType` and `TankTypeKind` in step
      // without the form asking for both.
      declared_type: draft.shape === 'quantity' ? draft.declaredType : '',
      description: draft.description.trim(),
      rolls_up: draft.shape === 'quantity' && draft.rollsUp,
      ...(draft.shape === 'choice' ? { values: splitValues(draft.values) } : {}),
      ...(draft.shape === 'link' ? { link: draft.linkKind } : {}),
    }))
}

/**
 * What is wrong with the attribute rows, or `''`.
 *
 * Checked here rather than only at the server so the message arrives before the
 * request. The duplicate rule is the one worth catching early: two declarations
 * of one name on one part is a REPEAT, and the projection indexes repeats - the
 * second value lands at `name#2` where nothing reads it.
 */
export function attributeRowsProblem(drafts: readonly AttributeDraft[]): string {
  const filled = drafts.filter((draft) => draft.name.trim() !== '')
  const untyped = filled.filter(
    (draft) => draft.shape === 'quantity' && !draft.declaredType,
  )
  if (untyped.length > 0) {
    return `Choose what kind of quantity ${untyped[0].name.trim()} is.`
  }
  const unlinked = filled.find((draft) => draft.shape === 'link' && !draft.linkKind)
  if (unlinked) {
    return `Choose what ${unlinked.name.trim()} links to.`
  }
  // A choice with nothing to choose from renders as free text, which is the
  // control it exists to replace.
  const empty = filled.find(
    (draft) => draft.shape === 'choice' && splitValues(draft.values).length === 0,
  )
  if (empty) {
    return `List the values ${empty.name.trim()} can take, separated by commas.`
  }
  const badValue = filled
    .filter((draft) => draft.shape === 'choice')
    .flatMap((draft) => splitValues(draft.values))
    .find((value) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(value))
  if (badValue) {
    return `${badValue} cannot be a value. Each one is written as a name.`
  }
  const named = filled.map((draft) => draft.name.trim())
  const repeated = named.find((name, index) => named.indexOf(name) !== index)
  if (repeated) {
    return `${repeated} is named twice. An attribute is declared once.`
  }
  const bad = filled.find((draft) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(draft.name.trim()))
  if (bad) {
    return (
      `${bad.name.trim()} is not a usable name. Use letters, digits and ` +
      'underscores, starting with a letter.'
    )
  }
  return ''
}

/** A blank form, keeping the package so several definitions can be made in a row. */
/**
 * What a new part definition specializes unless somebody says otherwise.
 *
 * The root of the profile: every quantity the library declares hangs off this
 * type or one below it. Spelled BARE rather than qualified because that is what
 * the picker's options and the attributes route both key on, and the server
 * resolves it to a qualified reference when it writes the declaration.
 */
export const DEFAULT_BASE = 'EchelonPart'

export function emptyForm(pkg = ''): WizardForm {
  return {
    package: pkg,
    identifier: '',
    name: '',
    abstract: false,
    instance: false,
    // EVERY ECHELON PART SPECIALIZES THIS, and a part def that specializes
    // nothing is not one: it carries no profile attribute, so a mass typed onto
    // it is written and then skipped by every rollup - stored, invisible, and
    // wrong in the direction nobody checks.
    //
    // It is also what stops the form opening with all thirty-odd attributes: the
    // list narrows to what the chosen type can carry, and with nothing chosen
    // there was nothing to narrow by. Anything more specific is one change of
    // the picker away.
    specializes: DEFAULT_BASE,
    parentPart: '',
    // One blank row, because an empty table offers nothing to type into and the
    // row is dropped from the request if it stays blank.
    childParts: [emptyChildDraft('row-0')],
    extras: {},
    // One blank row, for the same reason childParts has one: an empty table
    // offers nothing to type into, and a row left blank is dropped.
    newAttributes: [emptyAttributeDraft('attr-0')],
  }
}

/**
 * The registry fields the wizard offers beyond its own six questions.
 *
 * Taken from `/api/schema` rather than listed here, so a field added to the part
 * kind appears without this file being touched. Four are excluded and each for
 * its own reason: `identifier` and `name` are the wizard's own controls;
 * `type_name` is REFUSED by `POST /api/parts` with a 422 and `specializes`
 * replaces it; and a derived field is computed rather than stated, so an editable
 * control on one would be a promise the API breaks. Lists and references are left
 * out as well - creation takes the declaration, and a reference is an edit.
 *
 * `multiplicity` is excluded for the same reason it left the definitions table:
 * it characterises a USAGE inside its parent - four of these thrusters - and this
 * wizard makes a DEFINITION. Offering it here asks how many of a type there are,
 * which is not a question the type can answer. Each child row carries its own
 * multiplicity instead, which is where the fact belongs.
 *
 * @param kind the `design_part` entry of the registry
 */
export function wizardFields(
  kind: KindSchema,
  /**
   * The attributes the chosen type can carry, from `GET /api/parts/{id}/attributes`.
   *
   * Undefined means "not asked yet, or nothing chosen to ask about", and keeps
   * every field - the form was this wide before and a wide form is better than
   * one silently missing the attribute somebody came to state.
   */
  applicable?: readonly string[] | null,
): FieldSchema[] {
  const own = new Set(['identifier', 'name', 'type_name', 'multiplicity'])
  const allowed = applicable ? new Set(applicable) : null
  return kind.fields.filter(
    (field) =>
      !own.has(field.name) &&
      !field.derived &&
      !isCostly(field) &&
      // A FIELD BINDING NO FEATURE is a column on the projection - the flags,
      // the description - and belongs to every part by construction. Only the
      // model-declared attributes are gated by type.
      (!allowed || !field.feature || allowed.has(attributeOf(field))),
  )
}

/**
 * What is in the way locally, or '' when nothing is.
 *
 * Only refusals that are certain without asking are decided here. Everything
 * else is left to the API and rendered from its answer, because a form that
 * guesses at the server's rules eventually forbids something the server allows.
 *
 * @param form what has been filled in
 */
export function localRefusal(form: WizardForm): string {
  if (form.abstract && form.parentPart) return ABSTRACT_HAS_NO_PARENT
  // The same contradiction as a parent part, and the API refuses it in the same
  // words: an abstract def is the statement that there ARE no instances.
  if (form.abstract && form.instance) return ABSTRACT_HAS_NO_INSTANCE
  // A half-filled child row, or a multiplicity that is not one. Children are
  // OPTIONAL here - a definition with nothing inside it is a fine thing to create
  // - so emptiness is not a refusal, which is why this asks for the problem
  // rather than for the dialog's readiness.
  return childRowsProblem(form.childParts)
}

/**
 * Why Create is not available yet, or '' when it is.
 *
 * `package` and `identifier` are the only required fields on the API, so they
 * are the only ones required here.
 *
 * @param form what has been filled in
 * @param permission what `useCanWrite()` answered
 */
export function readiness(
  form: WizardForm,
  permission: { allowed: boolean; reason: string },
): string {
  if (!permission.allowed) return permission.reason
  if (!form.package) return 'Choose the package the definition goes in.'
  if (!form.identifier.trim()) return 'Give the definition an ID.'
  return localRefusal(form)
}

/** The body `POST /api/parts` accepts. Mirrors `api.createPart`'s parameter. */
export interface CreatePartBody {
  package: string
  identifier: string
  name?: string
  abstract?: boolean
  specializes?: string
  instance?: boolean
  parent_part?: string
  /** Bare definitions, or `{definition, multiplicity}` - see `ChildPartRow`. */
  child_parts?: ChildPartRow[]
  /**
   * Attributes to DECLARE on the new definition.
   *
   * Declared after the definition exists and inside the same transaction, so a
   * refusal on the third does not leave the first two written. No value rides
   * along: the field does not exist until the declaration does.
   */
  attributes?: {
    name: string
    declared_type: string
    description: string
    rolls_up: boolean
  }[]
  statement?: string
  multiplicity?: string
  is_illustrative?: boolean
  /**
   * Real hardware. Stated positively, and omitted rather than sent false, so
   * absence and "not physical" are the same answer - which is what the profile's
   * `@EchelonSysML::Physical` marker means: present or not present.
   *
   * Listed here because `POST /api/parts` names every field it accepts and
   * refuses the rest, so a registry flag does NOT ride along unannounced on this
   * endpoint the way it does on the generated ones.
   */
  is_physical?: boolean
}

/**
 * Assemble the request, sending `specializes` and never `type_name`.
 *
 * Empty fields are omitted rather than sent blank: every field but `package` and
 * `identifier` is optional, and the minimal body produces a bare
 * `part def <SEK> Seeker;`, which is a perfectly good thing to want.
 *
 * The body is a value rather than something built inline at the call site so
 * that the one assertion that matters - `specializes` present, `type_name`
 * absent - can be made against a function rather than against a rendered form.
 *
 * @param form what has been filled in
 * @param fields the registry fields the wizard offered, from `wizardFields`
 */
export function buildCreateBody(form: WizardForm, fields: FieldSchema[]): CreatePartBody {
  const body: Record<string, unknown> = {
    package: form.package,
    identifier: form.identifier.trim(),
  }

  if (form.name.trim()) body.name = form.name.trim()
  if (form.abstract) body.abstract = true
  if (form.specializes) body.specializes = form.specializes
  // Sent only when asked for. The server defaults it to false, and an explicit
  // false on every request would be this side restating a default it does not own.
  if (form.instance) body.instance = true
  // Not sent alongside `abstract`: the API refuses the pair, and `localRefusal`
  // has already said so, so this is the belt to that pair of braces.
  if (form.parentPart && !form.abstract) body.parent_part = form.parentPart
  // Assembled by `childPartRows`, which drops a row nobody filled in and sends a
  // bare string where there is no multiplicity - because omitting a multiplicity
  // states NOTHING, which is the language default and is not `[1]`.
  const children = childPartRows(form.childParts)
  if (children.length) body.child_parts = children
  // Attributes to DECLARE, not values to state. Sent only when there are any,
  // so an ordinary creation's body is unchanged.
  const declaring = attributePayload(form.newAttributes)
  if (declaring.length) body.attributes = declaring

  for (const field of fields) {
    const value = form.extras[field.name]
    if (typeof value === 'boolean') {
      if (value) body[field.name] = true
    } else if (typeof value === 'number') {
      // A quantity, as the control hands it back. Sent even when it is 0: a part
      // that draws no power is a statement, and the one thing this must not do is
      // turn it back into "unset".
      body[field.name] = value
    } else if (typeof value === 'string' && value.trim()) {
      body[field.name] = value.trim()
    }
  }

  // Belt and braces, and worth the two lines: `type_name` is refused with a 422,
  // and a registry field reaching `extras` under that name - which is exactly
  // what a change to `wizardFields` would do - would turn every creation into a
  // rejected request.
  delete body.type_name

  return body as unknown as CreatePartBody
}

// ---------------------------------------------------------------------------
// What was created
// ---------------------------------------------------------------------------

/** One thing that was created, ready to show. */
export interface CreatedLine {
  /** The name as the SERVER reports it. Never derived. */
  name: string
  qualified_name: string
  /** What it is, in words - 'definition', or where the usage went. */
  note: string
}

/** The whole report: the definition, and every usage made along with it. */
export interface CreatedReport {
  definition: CreatedLine
  usages: CreatedLine[]
  /** Why the usage names are not the ones anybody typed, or '' when there are none. */
  caption: string
}

export const USAGE_NAMING =
  'A usage carries no ID of its own: BEST names it after the definition it is ' +
  'typed by, with a lowered first letter, and makes that name unique across the ' +
  'whole model — so a second usage of Guidance is guidance2. These are the names ' +
  'it chose.'

/**
 * What creating a part definition actually made.
 *
 * Worth showing rather than a bare "created": the definition is the only thing
 * the person named, and between one and several usages were made under names
 * only the server knows. Every name here is read off the response.
 *
 * @param result the answer from `POST /api/parts`
 */
export function describeCreated(result: CreatedPart): CreatedReport {
  return {
    definition: {
      name: result.identifier,
      qualified_name: result.qualified_name,
      note: result.is_abstract
        ? `${result.name || result.identifier} — a generic kind, to be specialized`
        : result.name || result.identifier,
    },
    usages: (result.usages ?? []).map((usage) => ({
      name: usage.name,
      qualified_name: usage.qualified_name,
      note: `a usage of ${usage.definition}, inside ${usage.owner}`,
    })),
    caption: (result.usages ?? []).length ? USAGE_NAMING : '',
  }
}

/**
 * One line for the toast, naming the usages rather than counting them.
 *
 * A count answers nothing: `guidance2` is a name somebody will search for, and
 * "3 usages" would send them looking for it in the model instead.
 *
 * @param result the answer from `POST /api/parts`
 */
export function summarizeCreated(result: CreatedPart): string {
  const usages = (result.usages ?? []).map((usage) => usage.name)
  if (!usages.length) return `${result.identifier} created`
  return `${result.identifier} created, with ${usages.join(', ')}`
}

/**
 * Why a new package cannot be created yet, or '' when it can.
 *
 * The parent is required by the API and refused with a reason worth repeating:
 * BEST cannot decide which FILE would own a root package, so a package is added
 * beneath an existing one. That is all the wizard ever needs.
 *
 * @param parent the qualified name of the package it goes in
 * @param name what to call it
 */
export function newPackageRefusal(parent: string, name: string): string {
  if (!parent) return NEW_PACKAGE_NEEDS_PARENT
  if (!name.trim()) return 'Give the new package a name.'
  return ''
}

/**
 * A part as a picker line: name, the ID it is sent as, the qualified name under
 * it, and a 'generic' chip for an abstract definition - the choice is informed,
 * not hidden. The box shows the ID once chosen, since that is what is sent.
 */
export function partOption(choice: PartChoice): PickerOption {
  return {
    value: choice.id,
    label: choice.name || choice.id,
    code: choice.id,
    detail: choice.qualified_name || undefined,
    tag: choice.isGeneric ? 'generic' : undefined,
  }
}

/**
 * A package as a picker line, indented on its SHOWN depth so the list reads as
 * the tree it is. Chosen, the box shows the qualified name - the name alone is
 * not unique across a program.
 */
export function packageOption(choice: PackageChoice): PickerOption {
  return { value: choice.qualified_name, label: choice.name, depth: choice.depth }
}
